import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';

const TrialContext = createContext({ cfg: null, trialMode: false, entryLabel: 'Free', refresh: () => {} });

/** Site-wide Free vs Trial mode (Admin → Free vs Trial). Loaded once; every "Free" marker reads `entryLabel` from here. */
export function TrialProvider({ children }) {
  const [cfg, setCfg] = useState(null);
  const refresh = useCallback(() => api.get('/api/trial/config').then((r) => setCfg(r.data)).catch(() => setCfg((c) => c || { mode: 'free' })), []);
  useEffect(() => { refresh(); }, [refresh]);
  const value = useMemo(() => {
    const trialMode = cfg?.mode === 'trial';
    return {
      cfg: cfg || {},
      loaded: !!cfg,
      trialMode,
      entryLabel: trialMode ? (cfg.label || 'Trial') : 'Free',
      price: cfg?.price?.text || 'CA$7',
      hours: cfg?.hours || 72,
      refresh,
    };
  }, [cfg, refresh]);
  return <TrialContext.Provider value={value}>{children}</TrialContext.Provider>;
}

export const useTrial = () => useContext(TrialContext);

/** Trial mode: "Free" labels in copy read as the trial tier ("Free plan" → "Trial plan", "free mock test" → "trial mock test"). */
export function trialSwap(text, label, trialMode = true) {
  if (!trialMode || typeof text !== 'string' || !text) return text;
  const low = label.toLowerCase();
  return text
    .replace(/\bFree plan\b/g, `${label} plan`)
    .replace(/\bFree\b/g, label)
    .replace(/\bfree (plan|tier|account|mock|course|courses|class|classes|lesson|lessons|group|student|students|material|exam|exams|test|tests|version)\b/gi, (m, w) => `${m[0] === 'F' ? label : low} ${w}`);
}

/** Where a signed-in student should go next, honouring the trial lock. */
export function trialLocked(user) {
  return !!(user?.trial?.locked);
}

/** Paths a locked trial student may still open (pay, settings, help). */
export const TRIAL_OPEN_PATHS = ['/checkout', '/subscription', '/settings', '/my-tickets', '/contact', '/notifications', '/pricing', '/whats-included', '/connect-whatsapp', '/referral', '/auth'];
export const isTrialOpenPath = (path) => TRIAL_OPEN_PATHS.some((p) => path === p || path.startsWith(p + '/') || path.startsWith(p + '?'));
